import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import DashboardHero from '../../components/DashboardHero';
import UpcomingEvents from '../../components/UpcomingEvents';
import QuickActions from '../../components/QuickActions';
import useAuthStore from '../../store/authStore';
import { userService, enrollmentService } from '../../services/api';
import { StatCard, FullPageSpinner, HorizontalBarChart, TrendChart } from '../../components/ui';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    userService.getDashboard().then((res) => {
      if (mounted) setStats(res.data);
    }).catch(() => {}).finally(() => {
      if (mounted) setLoading(false);
    });
    enrollmentService.getEvents().then((res) => {
      if (mounted) setEvents(res.data.results || res.data || []);
    }).catch(() => {}).finally(() => {
      if (mounted) setEventsLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  if (loading || !user) return <FullPageSpinner />;

  const s = stats?.stats || {};
  const analytics = stats?.analytics || {};
  const formatDailySeries = (series = []) => series.map((point) => ({
    label: new Date(`${point.date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    value: point.value,
  }));
  const enrollmentTrend = formatDailySeries(analytics.enrollments_daily);
  const userTrend = formatDailySeries(analytics.users_daily);
  const quizAttemptTrend = formatDailySeries(analytics.quiz_attempts_daily);

  const topTiles = [
    {
      label: t('Total Users'), value: s.total_users ?? '—', color: 'blue', onClick: () => navigate('/admin/users'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" /></svg>,
    },
    {
      label: t('Pending Teacher Approvals'), value: s.pending_teacher_approvals ?? '—', color: 'yellow', onClick: () => navigate('/admin/teacher-approvals'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    },
    {
      label: t('Pending Enrollments'), value: s.pending_enrollments ?? '—', color: 'purple', onClick: () => navigate('/admin/enrollments?status=pending'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
    },
    {
      label: t('Pending Reports'), value: s.pending_reports ?? '—', color: 'red', onClick: () => navigate('/admin/moderation'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
    },
    { label: t('Teachers'), value: s.total_teachers ?? '—', color: 'blue' },
    { label: t('Students'), value: s.total_students ?? '—', color: 'green' },
    { label: t('Parents'), value: s.total_parents ?? '—', color: 'purple' },
    { label: t('Active Enrollments'), value: s.active_enrollments ?? '—', color: 'green' },
    {
      label: t('Courses'), value: s.total_modules ?? '—', color: 'blue', onClick: () => navigate('/admin/classes'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
    },
    { label: t('Lessons'), value: s.total_lessons ?? '—', color: 'purple' },
    { label: t('Quizzes'), value: s.total_quizzes ?? '—', color: 'yellow' },
    { label: t('Questions'), value: s.total_discussion_threads ?? '—', color: 'red' },
    { label: t('New Users (7 days)'), value: s.new_users_7d ?? '—', color: 'blue', onClick: () => navigate('/admin/users') },
    { label: t('New Enrollments (7 days)'), value: s.new_enrollments_7d ?? '—', color: 'green' },
    { label: t('Published Courses'), value: s.published_modules ?? '—', color: 'purple', onClick: () => navigate('/admin/classes') },
    { label: t('Quiz Pass Rate (30 days)'), value: s.quiz_pass_rate_30d != null ? `${s.quiz_pass_rate_30d}%` : '—', color: 'yellow' },
  ];

  const userComposition = [
    { label: t('Teachers'), value: s.total_teachers ?? 0 },
    { label: t('Students'), value: s.total_students ?? 0 },
    { label: t('Parents'), value: s.total_parents ?? 0 },
  ];
  const enrollmentStatus = (analytics.enrollment_status || []).map((item) => ({
    label: t(item.label), value: item.value,
  }));
  const topCourses = (analytics.top_courses || []).map((item) => ({
    label: item.name, value: item.value,
  }));

  const quickActions = [
    { label: t('Manage Users'), color: 'primary', onClick: () => navigate('/admin/users') },
    { label: t('Review Teacher Approvals'), color: 'yellow', onClick: () => navigate('/admin/teacher-approvals') },
    { label: t('Moderation Queue'), color: 'red', onClick: () => navigate('/admin/moderation') },
    { label: t('Subscription Plans'), color: 'purple', onClick: () => navigate('/admin/subscriptions') },
    { label: t('Grade Levels'), color: 'green', onClick: () => navigate('/admin/grades') },
    { label: t('Browse Modules'), color: 'gray', onClick: () => navigate('/courses') },
  ];

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Admin Dashboard')} />
        <main className="flex-1 overflow-y-auto p-6">
          <DashboardHero
            greeting={t('Welcome back', { name: user.first_name })}
            subtitle={t('Running the whole platform')}
            stat={{ label: t('Total Users'), value: s.total_users ?? '—' }}
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {topTiles.map((tile) => (
              <StatCard key={tile.label} {...tile} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <QuickActions actions={quickActions} columns={2} />
            </div>
            <UpcomingEvents events={events} loading={eventsLoading} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-2xl shadow-card p-4">
              <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('User Composition')}</h3>
              <HorizontalBarChart bars={userComposition} valueSuffix="" />
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4">
              <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Enrollment Status')}</h3>
              <HorizontalBarChart bars={enrollmentStatus} valueSuffix="" />
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
              <h3 className="text-sm font-display font-bold text-gray-800">{t('Enrollments Over Time')}</h3>
              <p className="text-xs text-gray-500 mb-3">{t('Daily totals for the last 30 days, including today')}</p>
              <TrendChart points={enrollmentTrend} color="#6739e0" valueSuffix="" emptyLabel={t('No items found', { what: t('Enrollments Over Time') })} />
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4">
              <h3 className="text-sm font-display font-bold text-gray-800">{t('User Registrations')}</h3>
              <p className="text-xs text-gray-500 mb-3">{t('Daily totals for the last 30 days, including today')}</p>
              <TrendChart points={userTrend} color="#0f86c8" valueSuffix="" emptyLabel={t('No items found', { what: t('User Registrations') })} />
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4">
              <h3 className="text-sm font-display font-bold text-gray-800">{t('Quiz Attempts Over Time')}</h3>
              <p className="text-xs text-gray-500 mb-3">{t('Daily totals for the last 30 days, including today')}</p>
              <TrendChart points={quizAttemptTrend} color="#16a36a" valueSuffix="" emptyLabel={t('No items found', { what: t('Quiz Attempts') })} />
            </div>
            <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
              <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Top Courses by Active Enrollments')}</h3>
              <HorizontalBarChart bars={topCourses} valueSuffix="" max={Math.max(1, ...topCourses.map((item) => item.value))} emptyLabel={t('No items found', { what: t('Courses') })} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;
